import axios from 'axios';
import {
  ASSIGN_USER_TO_PROJECT,
  CREATE_COMPANY,
  GET_COMPANIES,
  GET_COMPANY_TYPES,
  GET_USERS,
  get_request_config, EDIT_PROJECT
} from '@/utils/system_constants/constant.js';
import alert_mixin from '@/utils/mixins/alert_mixin';

const parse_api_data = (response) => {
  const raw = response?.data;
  if (typeof raw === 'string') return JSON.parse(raw);
  return raw;
};

const format_date_long = (val) => {
  if (!val) return '—';
  const date = new Date(val);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const to_status_pill = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('warn')) return { label: 'WARNING', cls: 'bg-warning-subtle text-warning' };
  if (s.includes('health')) return { label: 'HEALTHY', cls: 'bg-success-subtle text-success' };
  if (s.includes('active')) return { label: 'ACTIVE', cls: 'bg-primary-subtle text-primary' };
  return {
    label: (status ? String(status).toUpperCase() : 'ACTIVE'),
    cls: 'bg-primary-subtle text-primary'
  };
};

const get_user_name = (user = {}) => {
  const full_name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full_name || user.email || 'Unnamed User';
};

const map_assigned_user = (profile = {}) => {
  const user = profile.user || profile;
  const role = user.role || {};

  return {
    id: profile.user_id || user.id,
    profile_id: profile.profile_id,
    name: get_user_name(user),
    email: user.email || '',
    role: role.role || role || 'User',
    is_active: profile.is_active ?? user.is_active
  };
};

const map_project = (project) => {
  const pill = to_status_pill(project?.status);

  return {
    id: project?.id,
    icon: 'bi-compass',
    icon_bg: 'entity-icon-surface',
    status: pill.label,
    status_class: pill.cls,
    title: project?.name || project?.project_name || 'Untitled Project',
    subtitle: project?.project_type?.name || project?.type?.name || '—',
    project_type_id:
      project?.project_type?.id ??
      project?.type?.id ??
      project?.project_type_id ??
      project?.type_id ??
      null,
    description: project?.project_description || project?.description || '',
    enable_compliance_task: Boolean(project?.enable_compliance_task),
    created: format_date_long(project?.date_created || project?.created_at),
    admin: project?.creator?.full_name || project?.admin?.full_name || '—',
    assigned_users_count: 0,
    assigned_users_loading: false
  };
};



export default {
  name: 'ProjectManagement',
  mixins: [alert_mixin],
  data() {
    return {
      metrics: { total_projects: 0, active_streams: 0, pending_syncs: 0 },
      is_loading: false,
      load_error: null,
      show_create_modal: false,
      create_error: null,
      project_name: '',
      project_description: '',
      selected_project_type: '',
      enable_compliance_task: false,
      project_types: [],
      project_types_loading: false,
      project_types_error: null,
      update_project_id: null,
      edit_project_name: '',
      edit_project_description: '',
      edit_selected_project_type: null,
      edit_enable_compliance_task: false,
      selected_project: null,
      assigned_users: [],
      assigned_users_loading: false,
      projects: []
    };
  },

  mounted() {
    this.get_project_types();
    this.fetch_projects();
  },

  methods: {
    view_project(project) {
      if (!project?.id) return;

      this.$router.push({
        name: 'EntityConfigs',
        query: {
          project_id: String(project.id),
          project_name: project.title || project.name || ''
        }
      });
    },
    get_project_types() {
      return axios.get(GET_COMPANY_TYPES, get_request_config())
        .then(response => {
          const parsed_response = parse_api_data(response);
          if (parsed_response.data && parsed_response.data.length > 0) {
            this.project_types = parsed_response.data;
          } else {
            this.show_error('No project types found');
          }
        })
        .catch(error => {
          this.show_error('Error getting project types: ' + error);
        });
    },

    create_project() {
      const project_name = this.project_name;
      const project_description = this.project_description;
      const project_type = this.selected_project_type;

      if (!project_name || !project_name.trim()) {
        this.show_error('Project name must not be empty');
        return Promise.resolve();
      }
      if (!project_description || !project_description.trim()) {
        this.show_error('Project description must not be empty');
        return Promise.resolve();
      }
      if (!project_type || project_type < 1) {
        this.show_error('Please select a valid project type');
        return Promise.resolve();
      }

      const payload = {
        project_name,
        project_description,
        project_type_id: project_type,
        enable_compliance_task: this.enable_compliance_task,
      };

      return this.show_loader('Creating project...')
        .then(loader => {
          return axios.post(CREATE_COMPANY, payload, get_request_config())
            .then(() => {
              return this.fetch_projects()
                .then(() => {
                  this.project_name = '';
                  this.project_description = '';
                  this.selected_project_type = '';
                  this.enable_compliance_task = false;
                  this.close_modal('create_project');
                  this.close_loader(loader);
                });
            })
            .catch(error => {
              this.show_error('Error creating project: ' + error);
              this.close_loader(loader);
            });
        });
    },

    async fetch_projects() {
      this.is_loading = true;
      this.load_error = null;

      try {
        const response = await axios.get(GET_COMPANIES, get_request_config());
        const parsed = parse_api_data(response);

        const list = parsed?.data || parsed?.projects || [];
        const projects = Array.isArray(list) ? list : [];

        this.projects = projects.map(map_project).filter(p => p.id != null);
        this.metrics.total_projects = this.projects.length;
        await this.load_project_assigned_user_counts();

        if (parsed?.metrics) {
          this.metrics.active_streams =
            parsed.metrics.active_streams ?? this.metrics.active_streams;
          this.metrics.pending_syncs =
            parsed.metrics.pending_syncs ?? this.metrics.pending_syncs;
        }

        const highlight_id = this.$route?.query?.highlight;
        if (highlight_id != null) {
          this.$nextTick(() => {
            const el = document.getElementById(`project-${highlight_id}`);
            if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const message = err?.response?.data?.message;
        this.load_error = message || detail || 'Failed to load projects.';
      } finally {
        this.is_loading = false;
      }
    },

    async fetch_assigned_users(project_id) {
      const response = await axios.get(
        GET_USERS,
        get_request_config({
          params: {
            project_id: project_id,
            page: 1,
            page_size: 100
          }
        })
      );
      const response_data = parse_api_data(response);
      const items = Array.isArray(response_data.data) ? response_data.data : [];

      return items.map(map_assigned_user).filter((user) => user.id != null);
    },

    async load_project_assigned_user_counts() {
      const results = await Promise.allSettled(
        this.projects.map(async (project) => {
          project.assigned_users_loading = true;
          const users = await this.fetch_assigned_users(project.id);
          project.assigned_users_count = users.length;
          project.assigned_users_loading = false;
        })
      );

      results.forEach((result, index) => {
        if (result.status === 'rejected' && this.projects[index]) {
          this.projects[index].assigned_users_loading = false;
          this.projects[index].assigned_users_count = 0;
        }
      });
    },

    assigned_users_label(project) {
      if (project.assigned_users_loading) return 'Loading assigned users...';

      const count = Number(project.assigned_users_count || 0);
      return `${count} Assigned User(s)`;
    },

    async open_assigned_users_modal(project) {
      if (!project?.id) return;

      this.selected_project = project;
      this.assigned_users = [];
      this.assigned_users_loading = true;

      try {
        this.assigned_users = await this.fetch_assigned_users(project.id);
        project.assigned_users_count = this.assigned_users.length;
      } catch (error) {
        this.show_error('Failed to load assigned users.');
      } finally {
        this.assigned_users_loading = false;
      }
    },

    unassign_user_from_project(user) {
      if (!this.selected_project?.id || !user?.id) return;

      this.show_confirm(
        'Unassign User',
        `Are you sure you want to unassign ${user.name} from ${this.selected_project.title}?`,
        async () => {
          const assigner_id = this.$store.getters.currentUserId;
          if (!assigner_id) {
            this.show_error('Current user could not be resolved.');
            return;
          }

          const loader = await this.show_loader('Unassigning user...');

          try {
            const response = await axios.post(
              ASSIGN_USER_TO_PROJECT,
              {
                project: Number(this.selected_project.id),
                assigner: Number(assigner_id),
                assignee: Number(user.id)
              },
              get_request_config()
            );
            const response_data = parse_api_data(response);

            if (response_data?.status && response_data.status !== 'success') {
              this.show_error(response_data.message || 'Unassign failed.');
              return;
            }

            this.assigned_users = this.assigned_users.filter(
              (assigned_user) => String(assigned_user.id) !== String(user.id)
            );
            this.selected_project.assigned_users_count = this.assigned_users.length;
            this.show_success(response_data.message || 'User unassigned successfully.');
          } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Failed to unassign user.';
            this.show_error(message);
          } finally {
            this.close_loader(loader);
          }
        }
      );
    },

    open_edit_project_modal(project){
      if (!project) return;

      this.update_project_id = project.id;
      this.edit_project_name = project.title;
      this.edit_project_description = project.description;
      this.edit_selected_project_type = project.project_type_id || null;
      this.edit_enable_compliance_task = Boolean(project.enable_compliance_task);
    },

    async update_project(){
      if (!this.edit_project_name || !this.edit_project_name.trim()) {
        this.show_error('Project name must not be empty');
        return;
      }

      if (!this.edit_project_description || !this.edit_project_description.trim()) {
        this.show_error('Project description must not be empty');
        return;
      }

      if (!this.edit_selected_project_type) {
        this.show_error('Please select a valid project type');
        return;
      }

      const payload = {
        project_id: this.update_project_id,
        project_name: this.edit_project_name,
        project_description: this.edit_project_description,
        project_type_id: Number(this.edit_selected_project_type),
        enable_compliance_task: this.edit_enable_compliance_task,
      };

      const loader = await this.show_loader('Updating project...');

      try {
        await axios.post(EDIT_PROJECT, payload, get_request_config());

        this.show_success("Project updated successfully");
        this.close_modal("edit_project");

        await this.fetch_projects();

      } catch (error) {
        this.show_error("Error updating project: " + error);
      } finally {
        this.close_loader(loader);
      }
    },

  }
};
